import { Lightbulb } from "lucide-react";
import {
  useState, useEffect, useRef, Suspense,
} from "react";
import { Outlet } from "react-router";
import yeatiHello from "@/assets/yeati-hello-transparent.png";
import ChangelogPopup from "@/features/changelogs/components/ChangelogPopup";
import HelpButton from "@/features/help/components/HelpButton";
import JobModal from "@/features/jobs/components/JobModal";
import SuggestionsDrawer from "@/features/suggestions/components/SuggestionsDrawer";
import { Banner } from "@/shared/components/Banner";
import { ImpersonationBanner } from "@/shared/components/ImpersonationBanner";
import Search from "@/shared/components/Search";
import SearchBar from "@/shared/components/SearchBar";
import AppSidebar from "@/shared/components/Sidebar";
import { TranslationAlert } from "@/shared/components/TranslationAlert";
import { useOrganizationFeatures } from "@/shared/hooks/useOrganizationFeatures";
import { useSidebarReadyState } from "@/shared/hooks/useSidebarReadyState";
import { useTranslationDetection } from "@/shared/hooks/useTranslationDetection";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/Button";
import { Separator } from "@/shared/ui/Separator";
import { SidebarProvider, SidebarTrigger, SidebarInset } from "@/shared/ui/Sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/Tooltip";
import ContentLoadingScreen from "./ContentLoadingScreen";
import SidebarSkeleton from "./SidebarSkeleton";

const CONTENT_REVEAL_DELAY_MS = 400;

const Layout = ({ isOrganizationSettingsLayout = false }: { isOrganizationSettingsLayout?: boolean }) => {
  const [openSearch, setOpenSearch] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [showTranslationAlert, setShowTranslationAlert] = useState(false);
  const { isTranslationDetected } = useTranslationDetection();
  const { serviceSuggestions, serviceChangelog } = useOrganizationFeatures();

  const { isSidebarReady } = useSidebarReadyState();
  const [isContentVisible, setIsContentVisible] = useState(false);
  const hasCompletedInitialLoad = useRef(false);

  useEffect(() => {
    if (isTranslationDetected && !showTranslationAlert) {
      setShowTranslationAlert(true);
    }
  }, [isTranslationDetected, showTranslationAlert]);

  useEffect(() => {
    if (hasCompletedInitialLoad.current) return;

    if (isSidebarReady) {
      const timer = setTimeout(() => {
        setIsContentVisible(true);
        hasCompletedInitialLoad.current = true;
      }, CONTENT_REVEAL_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [isSidebarReady]);

  const skipLoadingSequence = hasCompletedInitialLoad.current;

  return (
    <>
      <TranslationAlert
        isVisible={showTranslationAlert}
        onClose={() => setShowTranslationAlert(false)}
      />
      <SidebarProvider defaultOpen className="h-svh max-h-svh">
        {!skipLoadingSequence && !isSidebarReady
          ? <SidebarSkeleton />
          : <AppSidebar />}

        <SidebarInset className={cn("px-4 py-2 overflow-y-auto", isOrganizationSettingsLayout && "pr-0 lg:overflow-hidden")}>
          {!skipLoadingSequence && !isContentVisible
            ? (
              <ContentLoadingScreen />
            )
            : (
              <div
                className={cn(
                  "flex min-h-0 flex-1 flex-col",
                  !skipLoadingSequence && "animate-in fade-in duration-500",
                )}
              >
                <header className="flex h-12 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
                  <div className="flex w-full items-center gap-2 px-2">
                    <SidebarTrigger className="-ml-1" />
                    <Separator
                      orientation="vertical"
                      className="mr-2 data-[orientation=vertical]:h-4"
                    />
                    <SearchBar onClick={() => setOpenSearch(true)} />
                    <Search
                      setOpenSearch={setOpenSearch}
                      openSearch={openSearch}
                    />
                    <div className="ml-auto flex items-center gap-2">
                      <HelpButton />
                      {serviceSuggestions && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className="size-7"
                              onClick={() => setSuggestionsOpen(true)}
                              aria-label="Mes suggestions"
                            >
                              <Lightbulb />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Mes suggestions</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                    <SuggestionsDrawer
                      open={suggestionsOpen}
                      onOpenChange={setSuggestionsOpen}
                    />
                  </div>
                </header>
                <div className="flex flex-1 flex-col gap-4 min-h-0">
                  <ImpersonationBanner />
                  <Banner
                    title="Découvrez Yeati+"
                    description="Votre tableau de bord admin ultra performant pour votre CSE !"
                    image={yeatiHello}
                    imageAlt="Yeati qui vous salue"
                    gradient={{
                      from: "#818cf8",
                      to: "#9333ea",
                    }}
                    actionText="En savoir plus"
                    actionUrl="https://yeati.co/"
                    storageKey="yeati_banner_closed"
                    useLocalStorage
                  />

                  <Suspense fallback={null}>
                    <Outlet />
                  </Suspense>
                  <JobModal />
                  {serviceChangelog && <ChangelogPopup />}
                </div>
              </div>
            )}
        </SidebarInset>
      </SidebarProvider>
    </>
  );
};

export default Layout;
